import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import Tesseract from "tesseract.js";
import "../../styling/OCRTextExtract.css";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

export default function OCRUploadPage() {
  const [file, setFile] = useState(null);
  const [fileUrl, setFileUrl] = useState("");

  // PDF viewer states
  const [numPages, setNumPages] = useState(null);
  const [pdfError, setPdfError] = useState(null);

  // Zoom
  const [zoom, setZoom] = useState(1.0);
  const MIN_ZOOM = 0.5;
  const MAX_ZOOM = 3.0;
  const ZOOM_STEP = 0.1;

  // OCR states
  const [isExtracting, setIsExtracting] = useState(false);
  const [progress, setProgress] = useState({ page: 0, total: 0, percent: 0 });
  const [ocrScale, setOcrScale] = useState(2.8);
  const [threshold, setThreshold] = useState(160); // set to "" to disable
  const [rawText, setRawText] = useState("");
  const [ocrError, setOcrError] = useState(null);

  const canvasRef = useRef(null);

  // Create/cleanup object URL
  useEffect(() => {
    if (!file) {
      setFileUrl("");
      setNumPages(null);
      setRawText("");
      setPdfError(null);
      setOcrError(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setFileUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const accept = useMemo(() => "application/pdf", []);

  const clampZoom = (value) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));
  const zoomIn = () =>
    setZoom((z) => clampZoom(parseFloat((z + ZOOM_STEP).toFixed(2))));
  const zoomOut = () =>
    setZoom((z) => clampZoom(parseFloat((z - ZOOM_STEP).toFixed(2))));

  const onFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    if (!f) return;

    if (f.type !== "application/pdf") {
      alert("Please upload a PDF file.");
      e.target.value = "";
      setFile(null);
      return;
    }
    setFile(f);
  };

  const clearFile = () => setFile(null);

  const onLoadSuccess = useCallback(({ numPages }) => {
    setNumPages(numPages);
    setPdfError(null);
  }, []);

  const onLoadError = useCallback(() => {
    setPdfError("Error loading PDF.");
  }, []);

  /**
   * ✅ NEW: Prefer native PDF text extraction when the PDF contains real text.
   * This yields PERFECT alignment for digital PDFs and avoids OCR entirely.
   */
  async function extractTextFromPdfPage(page) {
    const tc = await page.getTextContent();
    const items = (tc.items || [])
      .map((it) => ({
        text: (it.str || "").trim(),
        x: it.transform?.[4] ?? 0,
        y: it.transform?.[5] ?? 0,
      }))
      .filter((it) => it.text);

    // If very little text, likely scanned → return null to force OCR
    const charCount = items.reduce((s, it) => s + it.text.length, 0);
    if (charCount < 120) return null;

    // Group by y (PDF y increases upward), then sort by x for reading order
    items.sort((a, b) => b.y - a.y || a.x - b.x);

    const lines = [];
    const yTol = 2.5;

    for (const it of items) {
      let line = lines.find((l) => Math.abs(l.y - it.y) <= yTol);
      if (!line) {
        line = { y: it.y, parts: [] };
        lines.push(line);
      }
      line.parts.push(it);
    }

    lines.sort((a, b) => b.y - a.y);
    for (const l of lines) l.parts.sort((a, b) => a.x - b.x);

    return lines
      .map((l) =>
        l.parts
          .map((p) => p.text)
          .join(" ")
          .replace(/\s+/g, " ")
          .trim()
      )
      .filter(Boolean)
      .join("\n")
      .trim();
  }

  function buildFormTextFromWords(
    resultData,
    {
      rowGap = 12,
      colGap = 55,
      labelMaxWidth = 0.46,
      minLabelChars = 2,
    } = {}
  ) {
    const words = (resultData?.words || [])
      .filter((w) => (w.text || "").trim())
      .map((w) => ({
        text: (w.text || "").trim(),
        x0: w.bbox.x0,
        x1: w.bbox.x1,
        y0: w.bbox.y0,
        y1: w.bbox.y1,
        cx: (w.bbox.x0 + w.bbox.x1) / 2,
        cy: (w.bbox.y0 + w.bbox.y1) / 2,
      }));

    if (!words.length) return (resultData?.text || "").trim();

    const pageX0 = Math.min(...words.map((w) => w.x0));
    const pageX1 = Math.max(...words.map((w) => w.x1));
    const pageW = Math.max(1, pageX1 - pageX0);
    const labelBoundaryX = pageX0 + pageW * labelMaxWidth;

    words.sort((a, b) => {
      if (Math.abs(a.y0 - b.y0) > rowGap) return a.y0 - b.y0;
      return a.x0 - b.x0;
    });

    const rows = [];
    for (const w of words) {
      let row = rows.find((r) => Math.abs(r.y - w.y0) <= rowGap);
      if (!row) {
        row = { y: w.y0, items: [] };
        rows.push(row);
      }
      row.items.push(w);
    }
    rows.sort((a, b) => a.y - b.y);

    const rowToCells = (rowItems) => {
      const items = [...rowItems].sort((a, b) => a.x0 - b.x0);
      const cells = [];
      let cur = [];
      let prevX1 = null;

      for (const it of items) {
        if (prevX1 != null && it.x0 - prevX1 >= colGap) {
          cells.push(cur);
          cur = [];
        }
        cur.push(it);
        prevX1 = it.x1;
      }
      if (cur.length) cells.push(cur);

      const cellText = (cell) =>
        cell
          .map((x) => x.text)
          .join(" ")
          .replace(/\s+/g, " ")
          .trim();
      const cellX0 = (cell) => Math.min(...cell.map((x) => x.x0));
      const cellX1 = (cell) => Math.max(...cell.map((x) => x.x1));

      return cells
        .map((c) => ({
          text: cellText(c),
          x0: cellX0(c),
          x1: cellX1(c),
        }))
        .filter((c) => c.text);
    };

    const looksLikeLabel = (txt) => {
      const t = txt.trim();
      if (t.length < minLabelChars) return false;
      if (/:$/.test(t)) return true;
      if (
        /^(name|date|address|phone|email|ic|nr?ic|company|signature|to|from|vessel|voyage|destination|container|seal)\b/i.test(
          t
        )
      )
        return true;
      return false;
    };

    const out = [];
    let pending = null;

    for (const r of rows) {
      const cells = rowToCells(r.items);
      if (!cells.length) continue;

      if (cells.length === 1) {
        const only = cells[0];
        if (pending && only.x0 >= pending.valueX0 - 10) {
          out[out.length - 1] = out[out.length - 1] + " " + only.text;
          continue;
        }
        out.push(only.text);
        pending = null;
        continue;
      }

      const left = cells[0];
      const right = cells.slice(1);
      const rightText = right
        .map((c) => c.text)
        .join(" ")
        .replace(/\s+/g, " ")
        .trim();
      const valueX0 = Math.min(...right.map((c) => c.x0));

      const leftIsLabelZone = left.x1 <= labelBoundaryX;
      const leftIsLabel = looksLikeLabel(left.text) || leftIsLabelZone;

      if (leftIsLabel && rightText) {
        const label = left.text.replace(/\s+/g, " ").replace(/:?$/, ":").trim();
        out.push(`${label} ${rightText}`);
        pending = { label, valueX0 };
      } else {
        out.push(cells.map((c) => c.text).join("\t"));
        pending = null;
      }
    }

    return out.join("\n").trim();
  }

  /**
   * ✅ Your OCR runner:
   * - First try PDF-native extraction (best alignment).
   * - Fallback to OCR.
   * - Special-case Page 1 (tables): PSM=11, no threshold, and DO NOT run form aligner.
   * - Other pages: keep your original behavior (PSM=6 + buildFormTextFromWords).
   */
  const runOCR = useCallback(async () => {
    if (!fileUrl) return;

    setIsExtracting(true);
    setRawText("");
    setOcrError(null);

    try {
      const loadingTask = pdfjs.getDocument(fileUrl);
      const pdf = await loadingTask.promise;

      const rawOut = [];

      for (let i = 1; i <= pdf.numPages; i++) {
        setProgress({ page: i, total: pdf.numPages, percent: 0 });

        const page = await pdf.getPage(i);

        // ✅ 1) Prefer PDF text if present
        try {
          const pdfText = await extractTextFromPdfPage(page);
          if (pdfText) {
            rawOut.push(
              `\n\n=== Page ${i}/${pdf.numPages} ===\n\n${pdfText}`
            );
            continue;
          }
        } catch (e) {
          // If text extraction fails, fall back to OCR
          console.warn("PDF text extraction failed on page", i, e);
        }

        // ✅ 2) OCR fallback
        const isPage1 = i === 1;

        // ~300 DPI equivalent rendering for OCR
        const targetDpi = 300;
        const scale = (targetDpi / 72) * (ocrScale / 2);
        const viewport = page.getViewport({ scale, intent: "print" });

        if (!canvasRef.current)
          canvasRef.current = document.createElement("canvas");
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });

        ctx.imageSmoothingEnabled = false;
        canvas.width = viewport.width;
        canvas.height = viewport.height;

        await page.render({ canvasContext: ctx, viewport }).promise;

        // Thresholding:
        // - Keep your thresholding for pages 2..N (forms)
        // - Disable threshold on page 1 (table) because it often destroys thin text
        const cleaned =
          typeof threshold === "number" && !isPage1
            ? (() => {
                const c = document.createElement("canvas");
                c.width = canvas.width;
                c.height = canvas.height;

                const ictx = c.getContext("2d", { willReadFrequently: true });
                ictx.drawImage(canvas, 0, 0);

                const img = ictx.getImageData(0, 0, c.width, c.height);
                const d = img.data;

                for (let p = 0; p < d.length; p += 4) {
                  const y = 0.299 * d[p] + 0.587 * d[p + 1] + 0.114 * d[p + 2];
                  const v = y > threshold ? 255 : 0;
                  d[p] = d[p + 1] = d[p + 2] = v;
                }

                ictx.putImageData(img, 0, 0);
                return c;
              })()
            : canvas;

        const dataUrl = cleaned.toDataURL("image/png");

        const result = await Tesseract.recognize(dataUrl, "eng", {
          logger: (m) => {
            if (m.status === "recognizing text" && m.progress != null) {
              setProgress((p) => ({
                ...p,
                percent: Math.round(m.progress * 100),
              }));
            }
          },
          // ✅ key change: page 1 uses sparse mode (better for tables)
          tessedit_pageseg_mode: isPage1 ? 11 : 6,
        });

        // ✅ key change: do NOT apply form-alignment to page 1 table
        const pageRaw = isPage1
          ? (result.data?.text || "").trim()
          : buildFormTextFromWords(result.data, {
              rowGap: 12,
              colGap: 60,
              labelMaxWidth: 0.46,
            });

        rawOut.push(
          `\n\n=== Page ${i}/${pdf.numPages} ===\n\n${pageRaw.trim()}`
        );
      }

      setRawText(rawOut.join("\n").trim());
    } catch (e) {
      console.error(e);
      setOcrError("Error extracting text.");
    } finally {
      setIsExtracting(false);
      setProgress((p) => ({ ...p, percent: 100 }));
    }
  }, [fileUrl, ocrScale, threshold]);

  const copyTxt = async () => {
    if (!rawText) return;
    await navigator.clipboard.writeText(rawText);
  };

  const downloadTxt = () => {
    if (!rawText) return;
    const blob = new Blob([rawText], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "extracted_text.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="ocrx-root">
      {/* Header */}
      <div className="ocrx-header">
        <div>
          <h3 className="ocrx-title">OCR PDF Extractor (Local Only)</h3>
          <p className="ocrx-subtitle">
            Uses PDF text when available; OCR fallback with special handling for
            page 1 tables.
          </p>
        </div>

        <div className="ocrx-header-controls">
          <input
            className="ocrx-file-input"
            type="file"
            accept={accept}
            onChange={onFileChange}
          />

          {file && (
            <>
              <span className="ocrx-file-info">
                {file.name} ({Math.round(file.size / 1024)} KB)
              </span>
              <button
                className="ocrx-btn ocrx-btn-danger"
                type="button"
                onClick={clearFile}
              >
                Remove
              </button>
            </>
          )}

          <div className="ocrx-spacer" />

          <div className="ocrx-zoom">
            <button
              className="ocrx-btn"
              type="button"
              onClick={zoomOut}
              disabled={zoom <= MIN_ZOOM}
            >
              −
            </button>
            <input
              className="ocrx-zoom-input"
              type="number"
              min={MIN_ZOOM}
              max={MAX_ZOOM}
              step={ZOOM_STEP}
              value={zoom}
              onChange={(e) =>
                setZoom(clampZoom(parseFloat(e.target.value) || 1.5))
              }
            />
            <button
              className="ocrx-btn"
              type="button"
              onClick={zoomIn}
              disabled={zoom >= MAX_ZOOM}
            >
              +
            </button>
          </div>
        </div>
      </div>

      {/* Main */}
      {!fileUrl ? (
        <div className="ocrx-empty">
          Choose a PDF file to preview and extract text.
        </div>
      ) : (
        <div className="ocrx-split">
          {/* LEFT: PDF */}
          <div className="ocrx-left">
            {pdfError ? (
              <div className="ocrx-alert">{pdfError}</div>
            ) : (
              <div className="ocrx-pdf-pane">
                <Document
                  file={fileUrl}
                  onLoadSuccess={onLoadSuccess}
                  onLoadError={onLoadError}
                  loading={<div className="ocrx-muted">Loading PDF…</div>}
                >
                  {numPages &&
                    [...Array(numPages).keys()].map((n) => (
                      <div key={n} className="ocrx-page-wrap">
                        <Page
                          pageNumber={n + 1}
                          scale={zoom}
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                          renderMode="canvas"
                        />
                        <div className="ocrx-page-number">
                          Page {n + 1} of {numPages}
                        </div>
                      </div>
                    ))}
                </Document>
              </div>
            )}
          </div>

          {/* RIGHT: OCR */}
          <div className="ocrx-right">
            <div className="ocrx-ocr-controls">
              <label className="ocrx-field">
                OCR render scale
                <input
                  className="ocrx-input"
                  type="number"
                  min="0.5"
                  max="5"
                  step="0.1"
                  value={ocrScale}
                  onChange={(e) =>
                    setOcrScale(parseFloat(e.target.value) || 2.5)
                  }
                />
              </label>

              <label className="ocrx-field">
                Threshold (0–255 or blank)
                <input
                  className="ocrx-input"
                  type="number"
                  min="0"
                  max="255"
                  placeholder="e.g. 180"
                  value={threshold}
                  onChange={(e) => {
                    const v = e.target.value;
                    setThreshold(v === "" ? "" : Math.max(0, Math.min(255, +v || 0)));
                  }}
                />
              </label>

              <button
                className="ocrx-btn ocrx-btn-primary"
                type="button"
                onClick={runOCR}
                disabled={isExtracting}
              >
                {isExtracting ? "Extracting…" : "Extract Text"}
              </button>
            </div>

            {ocrError && <div className="ocrx-alert">{ocrError}</div>}

            {isExtracting && (
              <div className="ocrx-progress">
                Page {progress.page}/{progress.total} • {progress.percent}%
              </div>
            )}

            {rawText ? (
              <div className="ocrx-output-wrap">
                <div className="ocrx-actions">
                  <button className="ocrx-btn" type="button" onClick={copyTxt}>
                    Copy TXT
                  </button>
                  <button
                    className="ocrx-btn"
                    type="button"
                    onClick={downloadTxt}
                  >
                    Download .txt
                  </button>
                </div>
                <textarea className="ocrx-output" readOnly value={rawText} />
              </div>
            ) : (
              <div className="ocrx-hint">
                Click <b>Extract Text</b> to run extraction. Output will appear
                here.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
